import { EditOutlined } from '@ant-design/icons';
import { Alert, Drawer, Flex, Form, Input } from 'antd';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { UnresolvedVendorErrorCodeResponseDto } from '@evimo/client/models';
import { Button } from '~/app/components/common/Button';
import type {
  SelectOption,
  UnresolvedVendorErrorCodeFormValues,
} from '~/app/components/errorManagement/UnresolvedVendorErrorCodeTab/types';
import { toUnresolvedFormValues } from '~/app/components/errorManagement/UnresolvedVendorErrorCodeTab/utils';
import { VendorErrorCodeAdvancedFields } from '~/app/components/errorManagement/VendorErrorCodeFormSections/VendorErrorCodeAdvancedFields';
import { VendorErrorCodeRequiredFields } from '~/app/components/errorManagement/VendorErrorCodeFormSections/VendorErrorCodeRequiredFields';
import { ViewDrawerTitle } from '~/app/components/errorManagement/ViewDrawerTitle/ViewDrawerTitle';

export interface UnresolvedVendorErrorCodeDrawerProps {
  open: boolean;
  record: UnresolvedVendorErrorCodeResponseDto | null;
  manufacturerOptions: SelectOption[];
  assetTypeOptions: SelectOption[];
  submitting: boolean;
  hasConflict: boolean;
  onClose: () => void;
  onSave: (values: UnresolvedVendorErrorCodeFormValues) => Promise<void>;
  onReloadServerCopy: () => Promise<void>;
}

export function UnresolvedVendorErrorCodeDrawer({
  open,
  record,
  manufacturerOptions,
  assetTypeOptions,
  submitting,
  hasConflict,
  onClose,
  onSave,
  onReloadServerCopy,
}: UnresolvedVendorErrorCodeDrawerProps) {
  const { t } = useTranslation('errorManagement');
  const [form] = Form.useForm<UnresolvedVendorErrorCodeFormValues>();

  useEffect(() => {
    if (!open || !record) {
      return;
    }
    form.setFieldsValue(toUnresolvedFormValues(record));
  }, [open, record, form]);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={520}
      destroyOnClose
      styles={{
        header: {
          borderBottom: '2px solid var(--evimo-warning)',
        },
      }}
      title={
        <ViewDrawerTitle
          title={t('unresolved.drawer.title')}
          errorCode={record?.errorCode}
          accentColor="var(--evimo-warning)"
          accentBg="color-mix(in srgb, var(--evimo-warning) 12%, transparent)"
          icon={<EditOutlined />}
        />
      }
      extra={
        <Button
          type="primary"
          loading={submitting}
          onClick={() => form.submit()}
        >
          {t('unresolved.drawer.save')}
        </Button>
      }
    >
      {record ? (
        <Flex vertical gap={16}>
          {hasConflict ? (
            <Alert
              type="warning"
              showIcon
              message={t('unresolved.messages.conflict')}
              action={
                <Button size="small" onClick={() => onReloadServerCopy()}>
                  {t('unresolved.drawer.reloadServerCopy')}
                </Button>
              }
            />
          ) : null}

          <Form
            form={form}
            layout="vertical"
            onFinish={onSave}
            disabled={submitting}
            scrollToFirstError={{ behavior: 'smooth', block: 'center' }}
          >
            <Form.Item
              name="remark"
              label={t('unresolved.drawer.auditRemark')}
              extra={t('unresolved.drawer.auditRemarkHint')}
              rules={[
                {
                  validator: async (_, value) => {
                    if (!String(value ?? '').trim()) {
                      throw new Error(t('unresolved.messages.remarkRequired'));
                    }
                  },
                },
              ]}
            >
              <Input.TextArea
                rows={3}
                placeholder={t('unresolved.messages.remarkRequired')}
              />
            </Form.Item>

            <VendorErrorCodeRequiredFields
              manufacturerOptions={manufacturerOptions}
              assetTypeOptions={assetTypeOptions}
            />
            <VendorErrorCodeAdvancedFields />
            <Form.Item
              name="sourceDescription"
              label={t('drawer.sourceDescription')}
            >
              <Input.TextArea rows={3} />
            </Form.Item>
          </Form>
        </Flex>
      ) : null}
    </Drawer>
  );
}
